import React from 'react';
import moment from 'moment';
import identity from 'lodash/identity';
import { TextBadge, TextParagraph } from '../../../components/controls/Typography/TextBody';
import Heading from '../../../components/controls/Typography/Heading';
import useView from '../../../utils/useView';
import useAuthUtils from '../../../utils/useAuthUtils';

import './FormSidebar.less';

/**
 * @typedef {import('../../../store/reducers/dashboardReducer').TEvaluationRequest} TEvaluationRequest
 * @param {{recommendationRequest:TEvaluationRequest|null|undefined; classNames?:string}} props
 * @returns
 */
export const FormSidebar = (props) => {
  const { recommendationRequest, classNames } = props;
  const { onEnterKey } = useView();
  const auth = useAuthUtils();
  const programNamesEntries = Object.entries(recommendationRequest?.programNames ?? {});
  const [programsExpanded, setProgramsExpanded] = React.useState(true);

  const hasPrograms = programNamesEntries.length > 0;
  const isOwnEvaluationForm = recommendationRequest?.emailAddress === auth.account?.email.emailAddress;

  return (
    <div id="sidebar" className={["lsn-panel lsn-form-sidebar lsn-form-sidebar-panel-border", classNames].filter(identity).join(' ')}>

      <div className="panel lsn-form-sidebar-inner-panel">
        <Heading level={3} bold asSpan className='panel-title'>Request Information:</Heading>

        <div className="tile">
          <div className="tile-content" tabIndex={0}>
            <TextParagraph bold role='term'>Type:</TextParagraph>
            <TextParagraph className='mt-2' role='definition'>
              {recommendationRequest?.evaluationType}
            </TextParagraph>
          </div>
        </div>

        <div className='divider'></div>

        <div className="tile">
          <div className="tile-content" tabIndex={0}>
            <TextParagraph bold role='term'>Applicant Email:</TextParagraph>
            <TextParagraph className='mt-2' role='definition'>
              {recommendationRequest?.applicantEmail}
            </TextParagraph>
          </div>
        </div>

        <div className='divider'></div>

        <div className="tile">
          <div className="tile-content two-columns">
            <TextParagraph tabIndex={0}>
              <TextParagraph bold role={'term'}>Requested On: </TextParagraph>
              <TextParagraph className='mt-2' role={'definition'}>{moment(recommendationRequest?.requestedOnDate).format('MMM DD, YYYY')}</TextParagraph>
            </TextParagraph>
            <TextParagraph tabIndex={0}>
              <TextParagraph bold role={'term'}>Requested By: </TextParagraph>
              <TextParagraph className='mt-2' role={'definition'}>{moment(recommendationRequest?.requestedByDate).format('MMM DD, YYYY')}</TextParagraph>
            </TextParagraph>
          </div>
        </div>

        {
          !isOwnEvaluationForm ?
            <div className="tile">
              <div className="tile-content">
                <TextParagraph tabIndex={0}>
                  <TextParagraph bold role={'term'}>Request Sent To: </TextParagraph>
                  <TextParagraph className='mt-2' role={'definition'}>{recommendationRequest?.evaluatorName}</TextParagraph>
                </TextParagraph>
              </div>
            </div> : void 0}

        {
          recommendationRequest?.alternateId ?
            <div className="tile">
              <div className="tile-content">
                <TextParagraph tabIndex={0}>
                  <TextParagraph bold role={'term'}>{recommendationRequest?.alternateIdLabel}: </TextParagraph>
                  <TextParagraph className='mt-2' role={'definition'}>{recommendationRequest?.alternateId}</TextParagraph>
                </TextParagraph>
              </div>
            </div> : void 0}

        {
          recommendationRequest?.showOrgNameInLetters && recommendationRequest.organizationName && recommendationRequest.organizationLabel ?
            <div className="tile">
              <div className="tile-content">
                <TextParagraph tabIndex={0}>
                  <TextParagraph bold role={'term'}>{recommendationRequest?.organizationLabel}: </TextParagraph>
                  <TextParagraph className='mt-2' role={'definition'}>{recommendationRequest.organizationName}</TextParagraph>
                </TextParagraph>
              </div>
            </div> : void 0}

      </div>

      <div className="panel lsn-programs-sidebar lsn-form-sidebar-inner-panel">

        <Heading level={3} bold asSpan className='panel-title'>Applying To:</Heading>

        <div className="tile">
          <div className="tile-content" tabIndex={0}>
            <TextParagraph role='definition'>
              {recommendationRequest?.casName}
            </TextParagraph>
          </div>
        </div>

        {hasPrograms ? <div className={`tile expandable`}>
          <div className="tile-content">
            <TextParagraph
              role={'button'}
              bold
              tabIndex={0}
              className='lsn-programs-toggle-button'
              aria-controls={'programs_list'}
              aria-expanded={programsExpanded}
              onKeyDown={onEnterKey(() => setProgramsExpanded(!programsExpanded))}
              onClick={() => setProgramsExpanded(!programsExpanded)}
            >
              <span>Programs:</span>
              <TextBadge className='ml-2'>{programNamesEntries.flatMap(item => item[1].split('|')).length}</TextBadge>
              {programsExpanded ? <i className="icon icon-arrow-down color-main-primary"></i> : <i className="icon icon-arrow-right color-main-primary"></i>}
            </TextParagraph>
            <TextParagraph className='mt-2 ml-2' role={'region'} hidden={!programsExpanded} id={'programs_list'}>
              {programNamesEntries.length ?
                <div className='lsn-sidebar-programs-object' role={'list'}>
                  {programNamesEntries
                    .sort(([organizationA], [organizationB]) => organizationA.localeCompare(organizationB))
                    .map(([organization, programs]) => (
                      <div role={'listitem'} tabIndex={0}>
                        <TextParagraph className="lsn-sidebar-heading-tile mt-2" bold>{organization}:</TextParagraph>
                        {String(programs).split('|').map(program => <TextParagraph className="no-margin pl-2 mt-2">{program}</TextParagraph>)}
                      </div>
                    ))}
                </div> : null}
            </TextParagraph>
          </div>
        </div> : null}
      </div>

    </div>
  );
};

export default FormSidebar;
